import { facilityCenterTarget } from './facilityCoords';

/**
 * Day/night cycle — pure helpers driven entirely by the sim clock (state.tick).
 * 1 tick = 1 game hour, so the whole module is a function of `tick % 24`.
 * Nothing here reads or mutates game state; it just translates the hour-of-day
 * into lighting targets and human-readable labels.
 */

// Sun arc geometry (world units). The sun orbits the facility center in the
// x/y plane, offset south on z so it lights the buildings from the camera side.
const SUN_RADIUS = 30;
const SUN_PEAK_HEIGHT = 26;
const SUN_Z_OFFSET = 12;
const SUNRISE = 6;
const SUNSET = 18;

/** Returns the integer hour-of-day [0,24) for a given sim tick. */
export function hourOfDay(tick) {
  return ((tick % 24) + 24) % 24;
}

/** Formats an hour as a 24h clock string, e.g. 8 -> "08:00". */
export function formatClock(hour) {
  const h = Math.floor(hourOfDay(hour));
  return `${String(h).padStart(2, '0')}:00`;
}

/** Names the period of day for the badge label. */
export function timeOfDayLabel(hour) {
  const h = hourOfDay(hour);
  if (h < 5) return 'Night';
  if (h < 7) return 'Dawn';
  if (h < 12) return 'Morning';
  if (h < 17) return 'Afternoon';
  if (h < 19) return 'Dusk';
  if (h < 22) return 'Evening';
  return 'Night';
}

/** Sun glyph during daylight, moon otherwise. */
export function timeOfDayIcon(hour) {
  const h = hourOfDay(hour);
  return h >= SUNRISE && h < SUNSET ? '☀' : '🌙';
}

// ── Color keyframes ─────────────────────────────────────────────────────────
// Each keyframe: { hour, sun, ambient, sky } as hex strings + scalar intensities.
const KEYFRAMES = [
  { hour: 0,  sunColor: '#24304f', sunIntensity: 0.0, ambientColor: '#2a3a5c', ambientIntensity: 0.18, skyColor: '#0a1228', fogColor: '#070d1a', horizonColor: '#1a2344', nightFactor: 1.0, lampIntensity: 1.0 },
  { hour: 5,  sunColor: '#3a4a6b', sunIntensity: 0.0, ambientColor: '#3d4a6e', ambientIntensity: 0.22, skyColor: '#1a2344', fogColor: '#101828', horizonColor: '#2a3558', nightFactor: 0.85, lampIntensity: 0.7 },
  { hour: 6,  sunColor: '#ffb56b', sunIntensity: 0.45, ambientColor: '#caa890', ambientIntensity: 0.30, skyColor: '#ff9e6d', fogColor: '#c47858', horizonColor: '#ffb07a', nightFactor: 0.55, lampIntensity: 0.25 },
  { hour: 9,  sunColor: '#fff1de', sunIntensity: 0.80, ambientColor: '#c8d4e8', ambientIntensity: 0.34, skyColor: '#9ecbe8', fogColor: '#8eb8d4', horizonColor: '#c8e4f5', nightFactor: 0.15, lampIntensity: 0.0 },
  { hour: 12, sunColor: '#fff8f0', sunIntensity: 1.0, ambientColor: '#c8d4e8', ambientIntensity: 0.38, skyColor: '#87ceeb', fogColor: '#a8c8e0', horizonColor: '#d7eefc', nightFactor: 0.0,  lampIntensity: 0.0 },
  { hour: 15, sunColor: '#fff1de', sunIntensity: 0.82, ambientColor: '#c8d4e8', ambientIntensity: 0.34, skyColor: '#9ecbe8', fogColor: '#8eb8d4', horizonColor: '#c8e4f5', nightFactor: 0.15, lampIntensity: 0.0 },
  { hour: 18, sunColor: '#ff9e6d', sunIntensity: 0.45, ambientColor: '#caa890', ambientIntensity: 0.30, skyColor: '#ff9e6d', fogColor: '#c47858', horizonColor: '#ff9e6d', nightFactor: 0.55, lampIntensity: 0.35 },
  { hour: 19, sunColor: '#6a5a7a', sunIntensity: 0.0, ambientColor: '#4a4a6e', ambientIntensity: 0.24, skyColor: '#3a2f52', fogColor: '#2a2040', horizonColor: '#5a4070', nightFactor: 0.8,  lampIntensity: 0.75 },
  { hour: 24, sunColor: '#24304f', sunIntensity: 0.0, ambientColor: '#2a3a5c', ambientIntensity: 0.18, skyColor: '#0a1228', fogColor: '#070d1a', horizonColor: '#1a2344', nightFactor: 1.0, lampIntensity: 1.0 },
];

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

function rgbToHex([r, g, b]) {
  const toHex = (v) =>
    Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

function lerpHex(hexA, hexB, t) {
  const a = hexToRgb(hexA);
  const b = hexToRgb(hexB);
  return rgbToHex([lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]);
}

/** Finds the two keyframes bracketing `hour` and the blend factor between them. */
function bracket(hour) {
  for (let i = 0; i < KEYFRAMES.length - 1; i++) {
    const k0 = KEYFRAMES[i];
    const k1 = KEYFRAMES[i + 1];
    if (hour >= k0.hour && hour <= k1.hour) {
      const span = k1.hour - k0.hour;
      const t = span === 0 ? 0 : (hour - k0.hour) / span;
      return { k0, k1, t };
    }
  }
  const last = KEYFRAMES[KEYFRAMES.length - 1];
  return { k0: last, k1: last, t: 0 };
}

/** Computes the sun's world position for an hour, arcing east->overhead->west. */
function sunPosition(hour) {
  const [cx, , cz] = facilityCenterTarget();
  // Daylight progress 0..1 between sunrise and sunset; clamped at the horizon
  // outside daylight so night keeps the sun parked low (intensity is 0 anyway).
  const clamped = Math.max(SUNRISE, Math.min(SUNSET, hour));
  const t = (clamped - SUNRISE) / (SUNSET - SUNRISE); // 0 at sunrise, 1 at sunset
  const x = cx + Math.cos(t * Math.PI) * SUN_RADIUS;  // +R east -> -R west
  const y = Math.sin(t * Math.PI) * SUN_PEAK_HEIGHT;  // peaks at noon
  const z = cz + SUN_Z_OFFSET;
  return [x, y, z];
}

/**
 * Full lighting target for a given hour-of-day. The scene eases toward this
 * each frame (see FacilityLighting), so it never needs to be smooth itself.
 */
export function sunStateForHour(hour) {
  const h = hourOfDay(hour);
  const { k0, k1, t } = bracket(h);
  return {
    position: sunPosition(h),
    sunColor: lerpHex(k0.sunColor, k1.sunColor, t),
    sunIntensity: lerp(k0.sunIntensity, k1.sunIntensity, t),
    ambientColor: lerpHex(k0.ambientColor, k1.ambientColor, t),
    ambientIntensity: lerp(k0.ambientIntensity, k1.ambientIntensity, t),
    skyColor: lerpHex(k0.skyColor, k1.skyColor, t),
    fogColor: lerpHex(k0.fogColor, k1.fogColor, t),
    horizonColor: lerpHex(k0.horizonColor, k1.horizonColor, t),
    nightFactor: lerp(k0.nightFactor, k1.nightFactor, t),
    lampIntensity: lerp(k0.lampIntensity, k1.lampIntensity, t),
  };
}

export function nightFactorForHour(hour) {
  return sunStateForHour(hour).nightFactor;
}

export function windowEmissiveScale(hour) {
  const n = nightFactorForHour(hour);
  return 0.35 + n * 1.4;
}
